import type { $Fetch, FetchOptions, FetchRequest } from "ofetch";
import type { ApiResponse } from "~/types/ApiResponse";
import ApiError from "~/plugins/errors/apiError";
import type { LengthAwarePaginator } from "~/types/LengthAwarePaginator";
import type { Notification } from "#ui/types";
import type { User } from "~/models/User";

export type HttpMethod =
    | "GET"
    | "HEAD"
    | "POST"
    | "PUT"
    | "DELETE"
    | "CONNECT"
    | "OPTIONS"
    | "TRACE"
    | "PATCH";

export interface ApiServiceConfig {
    baseUri: string;
    client: $Fetch;
}

export interface EnhancedFetchOptions<T = any> {
    fetch?: Partial<FetchOptions & { method: HttpMethod }>;
    immediate?: boolean;
    server?: boolean;
    query?: Record<string, any> | Ref<Record<string, any>> | undefined;
    wait?: number | Ref<number> | undefined;
    body?: FetchOptions["body"] | Ref<FetchOptions["body"]>;
    watch?: (to: ApiResponse<T>, from: ApiResponse<T> | undefined) => void;
}

export interface PaginatedFetchOptions<T = any>
    extends EnhancedFetchOptions<LengthAwarePaginator<T>> {
    page?: number | Ref<number> | undefined;
    show?: number | Ref<number> | undefined;
}

export type EnhancedFetch<T> = ReturnType<
    typeof useAsyncData<ApiResponse<T>>
> & {
    data: Ref<ApiResponse<T>>;
    query: Ref<Record<string, any>>;
    waitTillDone: (callback: (response: ApiResponse<T>) => void) => void;
};

export type PaginatedFetch<T> = EnhancedFetch<LengthAwarePaginator<T>> & {
    page: Ref<number>;
    show: Ref<number>;
    items: ComputedRef<T[]>;
    total: ComputedRef<number>;
    isLastPage: ComputedRef<boolean | null>;
    isFirstPage: ComputedRef<boolean | null>;
    next: () => void;
    previous: () => void;
    first: () => void;
    last: () => void;
};

/**
 * This service will serve as the basis for
 * all backend API services. Each API service
 * will receive the base HTTP client in its
 * constructor. Here all common methods may be
 * defined.
 */
export class ApiBaseService {
    private readonly config: ApiServiceConfig;

    constructor(config: ApiServiceConfig) {
        this.config = config;
    }

    public request<T = any>(
        route: FetchRequest,
        options?: FetchOptions,
    ): Promise<T> {
        options = options || {};

        const user = useSanctumUser<ApiResponse<User>>();

        if (
            user.value?.extra?.api_token &&
            !options?.headers?.Authorization &&
            !options?.headers?.authorization
        ) {
            options.headers = {
                ...options.headers,
                Authorization: `Bearer ${user.value.extra.api_token}`,
            };
        }

        if (
            !options.headers ||
            (options.headers &&
                typeof options.headers === "object" &&
                !("X-Referer" in options.headers))
        ) {
            options.headers = {
                ...(options.headers ?? {}),
                "X-Referer": useRequestURL().href,
            };
        }

        try {
            const partnerContext = usePartnerContext();
            if (
                partnerContext.value &&
                (!options.headers ||
                    (options.headers &&
                        typeof options.headers === "object" &&
                        !("X-Partner-Context" in options.headers)))
            ) {
                options.headers = {
                    ...(options.headers ?? {}),
                    "X-Partner-Context": JSON.stringify(partnerContext.value),
                };
            }
        } catch (e) {
            console.error(e);
        }

        return this.config.client(route, {
            ...options,
            async onResponse({ response }) {
                if (response._data && response._data.navigate) {
                    navigateTo(response._data.navigate);
                }
            },
            async onResponseError({ response }) {
                if (import.meta.client && response.status === 500) {
                    const source = ref(JSON.stringify(response._data));
                    const clipboard = useClipboard({ source });
                    const notification: Partial<Notification> = {
                        id: "server_error",
                        title: "Server Error",
                        description:
                            response._data && response._data.message
                                ? response._data.message
                                : undefined,
                        icon: "i-heroicons-exclamation-triangle",
                        color: "red",
                        timeout: 5000,
                        actions:
                            typeof response._data === "object" &&
                            clipboard.isSupported.value
                                ? [
                                      {
                                          label: "Copy Error",
                                          click: () =>
                                              clipboard.copy(source.value),
                                      },
                                  ]
                                : [],
                        callback: () => {
                            useErrorNotification().value = false;
                        },
                    };

                    if (useErrorNotification().value) {
                        useToast().update("server_error", notification);
                    } else {
                        useErrorNotification().value = true;

                        useToast().add(notification);
                    }
                }

                if (response.status === 422) {
                    throw new ApiError(response._data, response);
                }
            },
        });
    }

    /**
     * Generates a safe route expectation.
     *
     * @param {string} name
     * @param {string} expected
     * @returns
     */
    route(name: string, expected: string, params?: Record<string, any>) {
        return params && Object.entries(params)
            ? expected.replace(/{(\w+)}/g, (_, key) => params[key] || "")
            : expected;
    }

    url(path: string) {
        const url = new URL(useRuntimeConfig().public.api.baseUrl);

        url.pathname = path;

        return url.href;
    }

    protected getConfig() {
        return this.config;
    }

    protected enhancedFetch<T = unknown, D = FetchOptions["body"]>(
        url: string | (() => string),
        options?: EnhancedFetchOptions<T>,
    ): EnhancedFetch<T> {
        const wait =
            options?.wait && (options.wait as Ref<number>).value
                ? (options.wait as Ref<number>)
                : ref<number>((options?.wait as number) ?? 250);
        const query: Ref<Record<string, any>> = options?.query?.value
            ? (options.query.value as Ref<Record<string, any>>)
            : ref<Record<string, any>>(options?.query ?? {});

        const body: Ref<D> = (options?.body as Ref<FetchOptions["body"]>)?.value
            ? (options?.body as Ref<FetchOptions["body"]> as Ref<D>)
            : (ref(
                  (options?.body ?? undefined) as FetchOptions["body"],
              ) as Ref<D>);

        const { data, pending, refresh, execute, error } = useAsyncData(
            `${options?.fetch?.method ?? "GET"}:${url}`,
            () =>
                this.request(typeof url === "function" ? url() : url, {
                    ...options?.fetch,
                    query: Object.keys(query.value) ? query.value : undefined,
                    body: body.value as FetchOptions["body"],
                }),
            {
                immediate: options?.immediate ?? true,
                server: options?.server ?? true,
            },
        );

        function makeWatcher() {
            return useDebounce(() => refresh(), wait.value, {
                trailing: true,
                leading: true,
            });
        }

        const watcher = makeWatcher();

        watch([query], watcher, {
            deep: true,
        });

        if (options && options.watch) {
            watch(data, (to, from) => {
                if (options && options.watch)
                    options.watch(
                        to as ApiResponse<T>,
                        from as ApiResponse<T> | undefined,
                    );
            });
        }

        watch(wait, () => makeWatcher());

        function waitTillDone(callback: (response: ApiResponse<T>) => void) {
            if (pending.value)
                watchOnce(pending, () =>
                    callback(data.value as ApiResponse<T>),
                );
            else callback(data.value as ApiResponse<T>);
        }

        return {
            data,
            pending,
            refresh,
            execute,
            error,
            query,
            waitTillDone,
        } as EnhancedFetch<T>;
    }

    protected paginate<T = unknown>(
        url: string | (() => string),
        options?: PaginatedFetchOptions<T>,
    ): PaginatedFetch<T> {
        const page =
            options?.page && (options.page as Ref<number>).value
                ? (options.page as Ref<number>)
                : ref<number>((options?.page as number) ?? 1);
        const show =
            options?.show && (options.show as Ref<number>).value
                ? (options.show as Ref<number>)
                : ref<number>((options?.show as number) ?? 25);
        const wait =
            options?.wait && (options.wait as Ref<number>).value
                ? (options.wait as Ref<number>)
                : ref<number>((options?.wait as number) ?? 250);
        const query: Ref<Record<string, any>> = options?.query?.value
            ? (options.query as Ref<Record<string, any>>)
            : ref<Record<string, any>>(options?.query ?? {});

        const { data, status, refresh, execute, error } = useAsyncData(
            typeof url === "function" ? url() : url,
            () =>
                this.request<ApiResponse<LengthAwarePaginator<T>>>(
                    typeof url === "function" ? url() : url,
                    {
                        ...options?.fetch,
                        query: {
                            ...(Object.keys(query.value) ? query.value : {}),
                            page: page.value,
                            show: show.value,
                        },
                    },
                ),
            {
                immediate: options?.immediate ?? true,
                server: options?.server ?? true,
            },
        );

        function makeWatcher() {
            return useDebounce(() => refresh(), wait.value, {
                trailing: true,
                leading: true,
            });
        }

        const watcher = makeWatcher();

        watch(page, watcher);

        watch(
            query,
            () => {
                page.value = 1;
                watcher();
            },
            {
                deep: true,
            },
        );

        if (options && options.watch) {
            watch(data, (to, from) => {
                if (options && options.watch)
                    options.watch(
                        to as ApiResponse<LengthAwarePaginator<T>>,
                        from as
                            | ApiResponse<LengthAwarePaginator<T>>
                            | undefined,
                    );
            });
        }

        watch(wait, () => makeWatcher());

        function waitTillDone(
            callback: (response: ApiResponse<LengthAwarePaginator<T>>) => void,
        ) {
            if (status.value === "pending")
                watchOnce(status, () =>
                    callback(
                        data.value as ApiResponse<LengthAwarePaginator<T>>,
                    ),
                );
            else callback(data.value as ApiResponse<LengthAwarePaginator<T>>);
        }

        return {
            data,
            pending: computed(() => status.value === "pending"),
            refresh,
            execute,
            error,
            query,
            page,
            show,
            status,
            items: computed(() => {
                if (!data.value) return [];

                return Array.isArray(data.value.data)
                    ? data.value.data
                    : (data.value.data?.data ?? []);
            }),
            firstPageUrl: computed(() =>
                data.value && data.value.data
                    ? data.value.data.first_page_url
                    : 0,
            ),
            currentPage: computed(() =>
                data.value && data.value.data
                    ? data.value.data.current_page
                    : page.value,
            ),
            from: computed(() =>
                data.value && data.value.data ? data.value.data.from : 0,
            ),
            lastPage: computed(() =>
                data.value && data.value.data ? data.value.data.last_page : 0,
            ),
            lastPageUrl: computed(() =>
                data.value && data.value.data
                    ? data.value.data.last_page_url
                    : "",
            ),
            nextPageUrl: computed(() =>
                data.value && data.value.data
                    ? data.value.data.next_page_url
                    : "",
            ),
            path: computed(() =>
                data.value && data.value.data ? data.value.data.path : "",
            ),
            perPage: computed(() =>
                data.value && data.value.data
                    ? data.value.data.per_page
                    : (query.value?.per_page ?? 0),
            ),
            prevPageUrl: computed(() =>
                data.value && data.value.data
                    ? data.value.data.prev_page_url
                    : 0,
            ),
            isLastPage: computed(
                () =>
                    data.value &&
                    data.value.data.current_page === data.value.data.last_page,
            ),
            total: computed(() => data.value?.data?.total ?? 0),
            isFirstPage: computed(
                () => data.value && data.value.data.current_page === 1,
            ),
            next() {
                if (
                    data.value?.data.last_page &&
                    page.value < data.value?.data.last_page
                )
                    page.value += 1;
            },
            previous() {
                if (page.value > 1) page.value -= 1;
            },
            first() {
                page.value = 1;
            },
            last() {
                page.value = data.value?.data.last_page ?? page.value;
            },
            waitTillDone,
        } as unknown as PaginatedFetch<T>;
    }
}
